import PropTypes from 'prop-types';
import { Icon } from '@eti/components';
import { seatMap } from '@eti/products';
import { DIRECTIONS, useDirection, useProperty, useTranslation } from '@eti/providers';
import { css, etiColors } from '@eti/styles';
import { BoundNew } from '@eti/types';
import { useHistory } from 'react-router-dom';
import { getBoundType } from '../../../../widgets/trip-details/utils/format';
import { scrollToElement } from '../../../../utils/scrollToElement';
import { routes } from '../../../../constants/routesAndApisConstants';
import ReviewModalSection from './ReviewModalSection';
import ReviewModalSeatTravelerInfo from './ReviewModalSeatTravelerInfo';

const { LTR } = DIRECTIONS;

const getDirection = (direction, isMultiStop, type) => {
  if (isMultiStop) {
    return direction !== LTR ? 'rotate(180deg)' : 'rotate(0deg)';
  }

  return (type !== 'inBound') !== (direction !== LTR) ? 'rotate(0)' : 'rotate(180deg)';
};

const headerStyles = css`
  align-items: center;
  background: #f5f5f5;
  color: ${etiColors.grayScale[10]};
  display: flex;
  font-size: 0.75rem;
  font-weight: 600;
  justify-content: start;
  padding: 7px;
`;

const originStyles = css`
  margin: 0 5px;
`;
const destinationStyles = css`
  margin: 0 5px;
`;

const unavailableSeatStyles = css`
  color: ${etiColors.black};
  font-size: 0.75rem;
  padding: 15px 40px;
`;

const iconClassName = (direction, isMultiStop, type) => css`
  transform: ${getDirection(direction, isMultiStop, type)};
`;

const ReviewModalChosenSeats = ({
  bounds = [],
  isMultiStop,
  seatMapSegments,
  setShowReviewModal,
  numberOfTravelers,
}) => {
  const { direction } = useDirection();
  const { p } = useProperty();
  const { t } = useTranslation();
  const history = useHistory();
  const getSegmentIndex = (segmentId) => (segmentId.length === 2 ? segmentId[1] : segmentId);
  const segments = bounds.flatMap((bound) =>
    bound.segments
      .filter((segment) => segment.__typename === 'TripSegment')
      .map((segment) => ({
        boundIndex: Number(bound.boundId),
        boundType: getBoundType(Number(bound.boundId) - 1),
        boundSegmentIndex: `${bound.boundId}-${Number(getSegmentIndex(segment.segmentId))}`,
        origin: {
          city: segment.origin.cityName,
          iataCode: segment.origin.code,
        },
        destination: {
          city: segment.destination.cityName,
          iataCode: segment.destination.code,
        },
      })),
  );

  const handleEditSeats = () => {
    setShowReviewModal(false);
    if (p('TravelerDetails.DynamicFlow.Enabled')) {
      history.push(routes.SEATING);
    } else {
      scrollToElement('.seatMap');
    }
  };

  const getSelectedSeatsNumber = (boundSegmentIndex) => {
    const firstPassengerSelectedSeat =
      seatMapSegments[boundSegmentIndex] &&
      Object.keys(seatMapSegments[boundSegmentIndex]).length === 1
        ? seatMapSegments[boundSegmentIndex][Object.keys(seatMapSegments[boundSegmentIndex])[0]]
            .value
        : '';
    if (firstPassengerSelectedSeat === null) {
      return null;
    }
    return `(${
      seatMapSegments[boundSegmentIndex]
        ? Object.keys(seatMapSegments[boundSegmentIndex]).length
        : 0
    }/${numberOfTravelers})`;
  };

  const getTextForSeatingPreferenceType = (optionType) => {
    switch (optionType) {
      case seatMap.seatingPreferenceValues.AISLE:
        return t('Product.SeatMap.Segment.Unavailable.SeatingCombo.Values.Aisle.Content');
      case seatMap.seatingPreferenceValues.WINDOW:
        return t('Product.SeatMap.Segment.Unavailable.SeatingCombo.Values.Window.Content');
      case seatMap.seatingPreferenceValues.BESIDE:
        return t('Product.SeatMap.Segment.Unavailable.SeatingCombo.Values.Beside.Content');
      case seatMap.seatingPreferenceValues.NO_OPTION:
        return t('Product.SeatMap.Segment.Unavailable.SeatingCombo.Values.AnySeat.Content');
      default:
        return t('Product.SeatMap.Summary.Segment.Unavailable');
    }
  };

  return (
    <ReviewModalSection
      button={{
        onClick: handleEditSeats,
        testId: 'travelerDetails-reviewModal-travelerInformation-editSeats',
        text: t('TravelerDetails.Review.Modal.Seating.Edit.Label'),
      }}
      dataTestId="review-modal-chosen-seats-section"
      title={t('TravelerDetails.Review.Modal.Seating.Label')}
    >
      {segments.map(({ boundSegmentIndex, boundType, destination, origin }) => (
        <div
          data-testid={`review-modal-chosen-seats-segment_${boundSegmentIndex}`}
          key={boundSegmentIndex}
        >
          <div className={headerStyles}>
            <Icon
              className={iconClassName(direction, isMultiStop, boundType)}
              color={etiColors.grayScale[10]}
              id="plane"
              size="1rem"
            />
            <div className={originStyles}>
              {origin.city} <span>{origin.iataCode}</span>
            </div>
            <span> – </span>
            <div className={destinationStyles}>
              {destination.city} <span>{destination.iataCode}</span>
            </div>
            <div>{getSelectedSeatsNumber(boundSegmentIndex)}</div>
          </div>
          <div>
            {seatMapSegments[boundSegmentIndex] ? (
              Object.entries(seatMapSegments[boundSegmentIndex]).map(([, traveler]) =>
                traveler.seatPreference ? (
                  <div className={unavailableSeatStyles} key={`${traveler.name}`}>
                    {getTextForSeatingPreferenceType(traveler.seatPreference)}
                  </div>
                ) : (
                  <ReviewModalSeatTravelerInfo key={`${traveler.name}`} {...traveler} />
                ),
              )
            ) : (
              <div className={unavailableSeatStyles}>
                {t('TravelerDetails.Review.Modal.Seating.Seat.NotSelected')}
              </div>
            )}
          </div>
        </div>
      ))}
    </ReviewModalSection>
  );
};

ReviewModalChosenSeats.propTypes = {
  bounds: PropTypes.arrayOf(BoundNew),
  isMultiStop: PropTypes.bool,
  numberOfTravelers: PropTypes.number,
  seatMapSegments: PropTypes.objectOf(PropTypes.any),
  setShowReviewModal: PropTypes.func,
};

export default ReviewModalChosenSeats;
